import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Home, CreditCard, RefreshCw, Wifi, WifiOff, BarChart3, Bitcoin, DollarSign, Clock } from 'lucide-react';
import { HolographicMetric } from '@/components/futuristic/HolographicMetric';
import { useMarketData } from '@/contexts/MarketDataContext';
import { useToast } from '@/components/ui/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { DataSourceTooltip } from './DataSourceTooltip';

export const LiveMarketDashboard: React.FC = () => {
  const { marketData, isConnected, error, subscribeToRates } = useMarketData();
  const { toast } = useToast();

  useEffect(() => {
    if (isConnected) {
      subscribeToRates();
    }
  }, [isConnected, subscribeToRates]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Market Data Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Calculate additional rates based on market data
  const getCalculatedRates = () => {
    if (!marketData) return null;

    const baseRate = marketData.interestRates['30_year_fixed'];
    
    return {
      helocRate: baseRate + 1.5, // Prime + margin simulation
      cashOutRefiRate: baseRate + 0.375, // Slightly higher than purchase
      jumboRate: baseRate + 0.25, // Premium for jumbo loans
    };
  };

  const calculatedRates = getCalculatedRates();

  const formatRate = (rate: number): string => `${rate.toFixed(3)}%`;

  const formatChange = (change: number): string => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  const formatStockPrice = (price: number): string => {
    return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatCryptoPrice = (price: number, decimals: number = 2): string => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  const getChangeColor = (change: number): "green" | "blue" | "purple" | "orange" | "pink" | "cyan" => {
    return change >= 0 ? "green" : "pink";
  };

  const getChangeIcon = (change: number) => {
    return change >= 0 ? TrendingUp : TrendingDown;
  };

  const getDataFreshness = (lastUpdated?: string) => {
    if (!lastUpdated) return { color: 'text-muted-foreground', label: 'Live' };
    
    const updateTime = new Date(lastUpdated);
    const hoursSinceUpdate = (Date.now() - updateTime.getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceUpdate < 1) {
      return { color: 'text-green-500', label: `Updated ${formatDistanceToNow(updateTime, { addSuffix: true })}` };
    } else if (hoursSinceUpdate < 24) {
      return { color: 'text-yellow-500', label: `Updated ${formatDistanceToNow(updateTime, { addSuffix: true })}` };
    } else {
      return { color: 'text-red-500', label: 'Stale data' };
    }
  };

  const getOverallSourceStatus = () => {
    if (!marketData?.sources) return { label: 'Unknown', isReal: false, color: 'text-muted-foreground' };
    
    const realSources = ['api_ninjas', 'fmp', 'coingecko', 'redfin', 'cache'];
    const { rates, stocks, crypto, trends } = marketData.sources;
    
    const allSources = [rates, stocks, crypto, trends];
    const realCount = allSources.filter(s => realSources.includes(s)).length;
    
    if (realCount === 4) return { label: 'All Real Data', isReal: true, color: 'text-green-500' };
    if (realCount >= 3) return { label: 'Mostly Real', isReal: true, color: 'text-green-500' };
    if (realCount > 0) return { label: 'Partial Real', isReal: true, color: 'text-yellow-500' };
    return { label: 'Mock Data', isReal: false, color: 'text-yellow-500' };
  };

  if (!marketData) {
    return (
      <div className="p-6 bg-background/50 backdrop-blur-sm border border-border/50 rounded-lg">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <h2 className="text-lg font-semibold text-foreground">Live Market Data</h2>
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <Wifi className="w-4 h-4 text-green-400" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-400" />
            )}
            <span className="text-xs text-muted-foreground">
              {isConnected ? 'Connected' : 'Connecting...'}
            </span>
          </div>
        </motion.div>

        <div className="space-y-6">
          {/* Interest Rates Loading */}
          <div>
            <div className="h-4 bg-muted-foreground/20 rounded w-24 mb-4 animate-pulse" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={`rate-${i}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 bg-background/30 backdrop-blur-sm border border-border/30 rounded-lg"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <RefreshCw className="w-4 h-4 text-muted-foreground animate-spin" />
                    <div className="h-3 bg-muted-foreground/20 rounded w-16 animate-pulse" />
                  </div>
                  <div className="h-6 bg-muted-foreground/20 rounded w-12 animate-pulse mb-1" />
                  <div className="h-2 bg-muted-foreground/20 rounded w-20 animate-pulse" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Stock Indices Loading */}
          <div>
            <div className="h-4 bg-muted-foreground/20 rounded w-32 mb-4 animate-pulse" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={`stock-${i}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 bg-background/30 backdrop-blur-sm border border-border/30 rounded-lg"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <RefreshCw className="w-4 h-4 text-muted-foreground animate-spin" />
                    <div className="h-3 bg-muted-foreground/20 rounded w-16 animate-pulse" />
                  </div>
                  <div className="h-6 bg-muted-foreground/20 rounded w-12 animate-pulse mb-1" />
                  <div className="h-2 bg-muted-foreground/20 rounded w-20 animate-pulse" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Cryptocurrency Loading */}
          <div>
            <div className="h-4 bg-muted-foreground/20 rounded w-28 mb-4 animate-pulse" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={`crypto-${i}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 bg-background/30 backdrop-blur-sm border border-border/30 rounded-lg"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <RefreshCw className="w-4 h-4 text-muted-foreground animate-spin" />
                    <div className="h-3 bg-muted-foreground/20 rounded w-16 animate-pulse" />
                  </div>
                  <div className="h-6 bg-muted-foreground/20 rounded w-12 animate-pulse mb-1" />
                  <div className="h-2 bg-muted-foreground/20 rounded w-20 animate-pulse" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const marketStats = [
    {
      title: '30-Year Fixed',
      value: formatRate(marketData.interestRates['30_year_fixed']),
      icon: Home,
      glowColor: 'cyan' as const
    },
    {
      title: 'VA 30-Year',
      value: formatRate(marketData.interestRates['va_30_year']),
      icon: TrendingUp,
      glowColor: 'green' as const
    },
    {
      title: 'HELOC Rate',
      value: calculatedRates ? formatRate(calculatedRates.helocRate) : 'N/A',
      icon: CreditCard,
      glowColor: 'purple' as const
    },
    {
      title: 'Cash-Out Refi',
      value: calculatedRates ? formatRate(calculatedRates.cashOutRefiRate) : 'N/A',
      icon: RefreshCw,
      glowColor: 'blue' as const
    }
  ];

  const stockStats = [
    {
      title: 'NASDAQ',
      value: formatStockPrice(marketData.stockIndices.nasdaq.price),
      change: marketData.stockIndices.nasdaq.changePercent,
      icon: BarChart3,
      glowColor: 'orange' as const
    },
    {
      title: 'S&P 500',
      value: formatStockPrice(marketData.stockIndices.sp500.price),
      change: marketData.stockIndices.sp500.changePercent,
      icon: TrendingUp,
      glowColor: 'pink' as const
    },
    {
      title: 'Dow Jones',
      value: formatStockPrice(marketData.stockIndices.dow.price),
      change: marketData.stockIndices.dow.changePercent,
      icon: BarChart3,
      glowColor: 'cyan' as const
    },
    {
      title: 'Russell 2000',
      value: formatStockPrice(marketData.stockIndices.russell2000.price),
      change: marketData.stockIndices.russell2000.changePercent,
      icon: TrendingUp,
      glowColor: 'green' as const
    }
  ];

  const cryptoStats = [
    {
      title: 'Bitcoin',
      value: formatCryptoPrice(marketData.cryptoPrices.btc.price, 2),
      change: marketData.cryptoPrices.btc.changePercent24h,
      icon: Bitcoin,
      glowColor: 'orange' as const
    },
    {
      title: 'Ethereum',
      value: formatCryptoPrice(marketData.cryptoPrices.eth.price, 2),
      change: marketData.cryptoPrices.eth.changePercent24h,
      icon: Bitcoin,
      glowColor: 'purple' as const
    },
    {
      title: 'USDT',
      value: formatCryptoPrice(marketData.cryptoPrices.usdt.price, 4),
      change: marketData.cryptoPrices.usdt.changePercent24h,
      icon: DollarSign,
      glowColor: 'green' as const
    },
    {
      title: 'USDC',
      value: formatCryptoPrice(marketData.cryptoPrices.usdc.price, 4),
      change: marketData.cryptoPrices.usdc.changePercent24h,
      icon: DollarSign,
      glowColor: 'blue' as const
    }
  ];

  return (
    <div className="p-6 bg-background/50 backdrop-blur-sm border border-border/50 rounded-lg">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <h2 className="text-lg font-semibold text-foreground">Live Market Data</h2>
        <div className="flex items-center gap-4">
          {marketData?.sources && (
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${getOverallSourceStatus().color}`}>
                {getOverallSourceStatus().label}
              </span>
              <DataSourceTooltip sources={marketData.sources} timestamps={marketData.timestamps} />
            </div>
          )}
          {marketData?.lastUpdated && (
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span className={`text-xs ${getDataFreshness(marketData.lastUpdated).color}`}>
                {getDataFreshness(marketData.lastUpdated).label}
              </span>
            </div>
          )}
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <Wifi className="w-4 h-4 text-green-400" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-400" />
            )}
            <span className="text-xs text-muted-foreground">
              {isConnected ? 'Live' : 'Disconnected'}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Interest Rates Section */}
      <div className="mb-6">
        <motion.h3
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-sm font-medium text-muted-foreground mb-4 flex items-center space-x-2"
        >
          <Home className="w-4 h-4" />
          <span>Interest Rates</span>
        </motion.h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {marketStats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <HolographicMetric
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                glowColor={stat.glowColor}
                animated={true}
                pulseEffect={false}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Stock Market Indices Section */}
      <div className="mb-6">
        <motion.h3
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="text-sm font-medium text-muted-foreground mb-4 flex items-center space-x-2"
        >
          <BarChart3 className="w-4 h-4" />
          <span>Market Indices</span>
        </motion.h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stockStats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
            >
              <HolographicMetric
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                glowColor={stat.glowColor}
                animated={true}
                pulseEffect={false}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Cryptocurrency Section */}
      <div className="mb-6">
        <motion.h3
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="text-sm font-medium text-muted-foreground mb-4 flex items-center space-x-2"
        >
          <Bitcoin className="w-4 h-4" />
          <span>Cryptocurrency</span>
        </motion.h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cryptoStats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <HolographicMetric
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                glowColor={stat.glowColor}
                animated={true}
                pulseEffect={false}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Market Trends Summary */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-border/30"
      >
        <div className="text-center">
          <div className="text-xs text-muted-foreground mb-1">Monthly Change</div>
          <div className={`flex items-center justify-center space-x-1 text-sm font-medium ${
            marketData.marketTrends.changeMonth >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {React.createElement(getChangeIcon(marketData.marketTrends.changeMonth), { className: 'w-3 h-3' })}
            <span>{formatChange(marketData.marketTrends.changeMonth)}</span>
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-xs text-muted-foreground mb-1">Annual Change</div>
          <div className={`flex items-center justify-center space-x-1 text-sm font-medium ${
            marketData.marketTrends.changeYear >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {React.createElement(getChangeIcon(marketData.marketTrends.changeYear), { className: 'w-3 h-3' })}
            <span>{formatChange(marketData.marketTrends.changeYear)}</span>
          </div>
        </div>
        
        <div className="text-center col-span-2 md:col-span-1">
          <div className="text-xs text-muted-foreground mb-1">Inventory</div>
          <div className="text-sm font-medium text-foreground">
            {marketData.marketTrends.inventoryMonths.toFixed(1)} months
          </div>
        </div>
      </motion.div>
    </div>
  );
};